"use client";

import {
	motion,
	useMotionTemplate,
	useMotionValue,
	useSpring,
} from "framer-motion";
import { useRef } from "react";

export const MagneticButton = ({
	children,
	className,
	onClick,
	type = "button",
	disabled = false,
}: {
	children: React.ReactNode;
	className?: string;
	onClick?: () => void;
	type?: "button" | "submit" | "reset";
	disabled?: boolean;
}) => {
	const ref = useRef<HTMLButtonElement>(null);

	const x = useMotionValue(0);
	const y = useMotionValue(0);

	const xSpring = useSpring(x, { mass: 0.1, stiffness: 150, damping: 12 });
	const ySpring = useSpring(y, { mass: 0.1, stiffness: 150, damping: 12 });

	const transform = useMotionTemplate`translateX(${xSpring}px) translateY(${ySpring}px)`;

	const handleMouseMove = (
		e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
	) => {
		if (!ref.current) return;

		const { height, width, left, top } = ref.current.getBoundingClientRect();

		const relativeX = e.clientX - (left + width / 2);
		const relativeY = e.clientY - (top + height / 2);

		const xRange = width / 2;
		const yRange = height / 2;

		const xMove = (relativeX / xRange) * 15; // 15px movement
		const yMove = (relativeY / yRange) * 15;

		x.set(xMove);
		y.set(yMove);
	};

	const handleMouseLeave = () => {
		x.set(0);
		y.set(0);
	};

	return (
		<motion.button
			ref={ref}
			type={type}
			disabled={disabled}
			onMouseMove={handleMouseMove}
			onMouseLeave={handleMouseLeave}
			onClick={onClick}
			style={{ transform }}
			className={className}
		>
			{children}
		</motion.button>
	);
};
